import React from 'react';

interface BookingCardProps {
  booking: {
    id: string;
    date_formatted: string;
    time_range: string;
    status: string;
    client: {
      name: string;
      email: string;
      phone?: string;
      is_contact?: boolean;
    } | null;
    service: {
      name: string;
      duration_min: number;
      price_cents: number;
      currency: string;
    } | null;
    total_price_cents: number;
    currency: string;
  };
  onAction: (action: string, bookingId: string) => void;
}

const BookingCard: React.FC<BookingCardProps> = ({ booking, onAction }) => {
  const formatPrice = (cents: number, currency: string) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800',
    };

    return (
      <span
        className={`px-3 py-1 text-xs font-medium rounded-full ${
          styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getActions = (status: string) => {
    const actions = [];

    if (status === 'pending') {
      actions.push({ label: 'Confirm', action: 'confirm', color: 'text-green-600 hover:text-green-700' });
    }

    if (status === 'pending' || status === 'confirmed') {
      actions.push({ label: 'Cancel', action: 'cancel', color: 'text-red-600 hover:text-red-700' });
      actions.push({ label: 'Edit', action: 'edit', color: 'text-purple-600 hover:text-purple-700' });
    }

    if (status === 'confirmed') {
      actions.push({ label: 'Complete', action: 'complete', color: 'text-blue-600 hover:text-blue-700' });
      actions.push({ label: 'No Show', action: 'no_show', color: 'text-gray-600 hover:text-gray-700' });
    }

    return actions;
  };

  const actions = getActions(booking.status);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        {/* Left side - Booking info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-semibold text-lg">
              {(booking.client?.name ?? '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{booking.client?.name ?? 'Unknown Client'}</h3>
              <p className="text-sm text-gray-500">{booking.client?.email ?? ''}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Service</p>
              <p className="font-medium text-gray-900">{booking.service?.name ?? 'Deleted Service'}</p>
              <p className="text-sm text-gray-500">{booking.service?.duration_min ?? '—'} min</p>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-1">Date & Time</p>
              <p className="font-medium text-gray-900">{booking.date_formatted}</p>
              <p className="text-sm text-gray-500">{booking.time_range}</p>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-4">
            <div>
              <p className="text-sm text-gray-600">Price</p>
              <p className="text-lg font-semibold text-purple-600">{formatPrice(booking.total_price_cents, booking.currency)}</p>
            </div>
          </div>
        </div>

        {/* Right side - Status and actions */}
        <div className="flex flex-col items-end gap-4">
          {getStatusBadge(booking.status)}

          {actions.length > 0 && (
            <div className="flex flex-col gap-2">
              {actions.map((action) => (
                <button
                  key={action.action}
                  onClick={() => onAction(action.action, booking.id)}
                  className={`text-sm font-medium ${action.color} hover:underline`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingCard;
